/**
 * AuthContext — owns B2C auth lifecycle + PortalIdentity resolve call.
 *
 * Flow:
 *   1. On mount → `initializeMsal()` → parse redirect → detect active account.
 *   2. If account present → call `client.portal.identity.resolve()` → map
 *      B2C `sub` to `PortalIdentityDto` (cifId may be null if not existing).
 *   3. Expose `{ identity, token, isAuthenticated, loading }`.
 *
 * Session timeout:
 *   - 55-min soft warning → SessionTimeoutModal (docs/gap-search §B.5 #6).
 *   - 60-min idle → forced logout.
 *   - 8-h absolute enforced server-side by B2C; mirrored here.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PortalIdentityDto } from '@retail-ops/shared-types';
import { initializeMsal, getMsalInstance, getAccessToken, logout as b2cLogout, login as b2cLogin } from '../services/b2c';
import { getApiClient } from '../services/apiClient';

interface AuthState {
  identity: PortalIdentityDto | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Reset the 55-min warning + 60-min idle timers. */
  touchActivity: () => void;
  /** Idle-timeout signal — wired by SessionTimeoutModal. */
  showIdleWarning: boolean;
  dismissIdleWarning: () => void;
}

const DEFAULT_AUTH_STATE: AuthState = {
  identity: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  login: async () => {},
  logout: async () => {},
  touchActivity: () => {},
  showIdleWarning: false,
  dismissIdleWarning: () => {},
};

const AuthCtx = createContext<AuthState>(DEFAULT_AUTH_STATE);

const IDLE_WARN_MS = 55 * 60 * 1000;
const IDLE_LOGOUT_MS = 60 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [identity, setIdentity] = useState<PortalIdentityDto | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);

  const warnTimerRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<number | null>(null);

  // -------------------------------------------------------------------
  // Timer management
  // -------------------------------------------------------------------
  function clearIdleTimers(): void {
    if (warnTimerRef.current !== null) {
      window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (logoutTimerRef.current !== null) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }

  function scheduleIdleTimers(): void {
    clearIdleTimers();
    warnTimerRef.current = window.setTimeout(() => {
      setShowIdleWarning(true);
    }, IDLE_WARN_MS);
    logoutTimerRef.current = window.setTimeout(() => {
      void b2cLogout();
    }, IDLE_LOGOUT_MS);
  }

  function touchActivity(): void {
    if (!isAuthenticated) return;
    setShowIdleWarning(false);
    scheduleIdleTimers();
  }

  function dismissIdleWarning(): void {
    setShowIdleWarning(false);
    scheduleIdleTimers();
  }

  // -------------------------------------------------------------------
  // Boot: init MSAL, resolve identity
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        await initializeMsal();
        const msal = getMsalInstance();
        const accounts = msal.getAllAccounts();
        if (accounts.length === 0) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setLoading(false);
          }
          return;
        }
        msal.setActiveAccount(accounts[0] ?? null);

        const token = await getAccessToken();
        if (!token) {
          if (!cancelled) {
            setIsAuthenticated(false);
            setLoading(false);
          }
          return;
        }

        const client = getApiClient();
        const resolved = await client.portal.identity.resolve({ signal: controller.signal });
        if (cancelled) return;
        if (resolved.success && resolved.data) {
          setIdentity(resolved.data);
          setIsAuthenticated(true);
          scheduleIdleTimers();
        } else {
          // TODO(PLANNED /portal/identity/resolve): surface a friendly error path.
          setError(resolved.error ?? 'Could not resolve identity.');
          setIsAuthenticated(true); // we have a token; server says no-CIF
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Auth init failed.');
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      clearIdleTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wire activity listeners once we're authenticated.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const handler = (): void => touchActivity();
    const evts: Array<keyof DocumentEventMap> = ['click', 'keydown', 'scroll', 'mousemove'];
    evts.forEach((e) => document.addEventListener(e, handler, { passive: true }));
    return () => {
      evts.forEach((e) => document.removeEventListener(e, handler));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const value = useMemo<AuthState>(
    () => ({
      identity,
      isAuthenticated,
      loading,
      error,
      login: async (returnTo?: string) => b2cLogin(returnTo),
      logout: async () => {
        clearIdleTimers();
        await b2cLogout();
      },
      touchActivity,
      showIdleWarning,
      dismissIdleWarning,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity, isAuthenticated, loading, error, showIdleWarning],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthCtx);
}
